import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { useAccessToken } from "./use-access-token";
import { callLcosService } from "../data/lcosServices";

export interface Skill {
  skillId: string;
  skillName: string;
  canTeachVirtual: boolean;
  canTeachInPerson: boolean;
  serviceName: string | null;
  group: string;
}

export interface SkillGroup {
  displayName: string;
  skills: Skill[];
  group?: SkillGroup[] | null;
}

export const useSkills = (centerId: string, userId: string) => {
  const queryClient = useQueryClient();
  const { getToken } = useAccessToken();

  const getSkillsData = async () => {
    return getSkills(await getToken(), centerId, userId);
  };

  const queryKey = ["skillsData", centerId, userId];

  const skillsQuery = useQuery({
    queryKey,
    queryFn: getSkillsData,
    enabled: !!centerId && !!userId
  });

  const updateSingleSkillMutator = useMutation({
    mutationFn: async (updatedSkill: Skill) => updateSingleSkill(await getToken(), centerId, userId, updatedSkill),

    onSuccess: newSkills => {
      queryClient.setQueryData(queryKey, newSkills);
    }
  });

  return {
    skills: skillsQuery.data || [],
    isLoading: skillsQuery.isLoading,
    updateSingleSkillMutator
  };
};

export const getSkills = (accessToken: string, centerId: string | undefined, userId: string | undefined) => {
  return callLcosService<SkillGroup[]>(accessToken, `/api/skills/${centerId}/${userId}`);
};

export const updateSingleSkill = (
  accessToken: string,
  centerId: string | undefined,
  userId: string | undefined,
  skill: Skill
) => {
  return callLcosService<SkillGroup[]>(accessToken, `/api/skills/${centerId}/${userId}`, "PUT", skill);
};
